import React, { useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { useGetCaptableInfo } from 'services/hooks/captable';
import CitationValueViewerProps from './CitationValueViewer.props';
import {
  StyledCitationNumber,
  StyledCitationNumberWrapper,
  StyledCitationValueViewer,
  StyledCitationValueViewerWrapper,
} from './CitationValueViewer.styles';
import CitationPopover from '../../../../../components/Popovers/citation-popover/CitationPopover';
import FastValueViewer from '../../../../../components/ScalarSpreadsheet/utilities/FastValueViewer';
import { EventType, useEventBus } from '../../../../../context';
import { useDocumentFootnotes } from '../../../../../context/DocumentFootnotesContext';
import { CitationReferenceData } from '../../../../../services/reference-generator/types';
import { useCitationContext } from '../captable-table/CitationContext';

const CitationValueViewer = ({
  value,
  citationReferences,
  citationNumber,
  originalValueViewer: OriginalValueViewer,
  citation,
  ...restProps
}: CitationValueViewerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hoveredCitation, setHoveredCitation] = useState<CitationReferenceData | undefined>(undefined);
  const [isHovered, setIsHovered] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { setActiveCitation, activeCitation } = useCitationContext();
  const { publish } = useEventBus();
  const { getSourceNumber } = useDocumentFootnotes();
  const [{ captableInfo }] = useStore() as unknown as UseStoreValues;
  const [, getCapTableInfo] = useGetCaptableInfo() as unknown as [unknown, (id: number) => Promise<void>];

  const citationSourceNumbers = useMemo(() => {
    const refs = restProps.cell.citationReferences;
    if (!refs || refs.length === 0) {
      return [];
    }

    const sourceNumbers: number[] = [];
    refs.forEach(cit => {
      const sourceNumber = getSourceNumber(cit.document_reference);
      if (sourceNumber !== undefined && !sourceNumbers.includes(sourceNumber)) {
        sourceNumbers.push(sourceNumber);
      }
    });

    return sourceNumbers.sort((a, b) => a - b);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citationReferences, getSourceNumber]);

  const memoCitations = useMemo(
    () => restProps.cell.citationReferences ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [citationReferences]
  );

  const scheduleClose = () => {
    closeTimerRef.current = setTimeout(() => {
      setHoveredCitation(undefined);
      setAnchorEl(null);
    }, 120);
  };

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const titleCell = useMemo(() => {
    const columnId = restProps.cell.columnLegend;
    const titleCellForColumn = restProps.cell.sheet?.cells?.[`${columnId}1`];
    return titleCellForColumn?.value ? (titleCellForColumn.value as string) : 'Unknown...';
  }, [restProps.cell]);

  const handleBadgeClick = (e: React.MouseEvent<HTMLElement>, cit: CitationReferenceData) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setHoveredCitation(cit);
  };

  const handleCitationDelete = (citationId: number) => {
    setHoveredCitation(undefined);
    setAnchorEl(null);
    setActiveCitation(undefined);
    setIsHovered(false);

    if (captableInfo?.id) {
      getCapTableInfo(captableInfo.id);
    }
  };

  const citationContainsActive = useMemo(() => {
    const refForActive = citationReferences?.find(cit => cit.id === activeCitation?.id);

    return Boolean(refForActive);
  }, [citationReferences, activeCitation]);

  if (!citationReferences || citationReferences.length === 0) {
    // If no citations, render original viewer or plain text
    if (OriginalValueViewer) {
      return <OriginalValueViewer value={value} {...restProps} />;
    }
    return <FastValueViewer value={value} {...restProps} />;
  }

  return (
    <StyledCitationValueViewerWrapper
      onMouseEnter={() => {
        cancelClose();
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        scheduleClose();
        setIsHovered(false);
      }}>
      {(isHovered || Boolean(hoveredCitation) || citationContainsActive) && (
        <StyledCitationNumberWrapper>
          {memoCitations.map(cit => {
            const isActiveOrHighlighted = hoveredCitation?.id === cit.id || activeCitation?.id === cit.id;

            return (
              <StyledCitationNumber
                key={cit.id}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => handleBadgeClick(e, cit)}
                sx={{
                  maxWidth: citationSourceNumbers.length > 1 ? '40px' : '24px',
                  backgroundColor: isActiveOrHighlighted ? 'primary.main' : 'primary.light',
                  color: isActiveOrHighlighted ? 'primary.contrastText' : 'primary.dark',
                  cursor: 'pointer',
                }}>
                {getSourceNumber(cit.document_reference)}
              </StyledCitationNumber>
            );
          })}
        </StyledCitationNumberWrapper>
      )}

      <StyledCitationValueViewer>
        {OriginalValueViewer ? (
          <OriginalValueViewer value={value} {...restProps} />
        ) : (
          <FastValueViewer className="text-cell" value={value} {...restProps} />
        )}
      </StyledCitationValueViewer>

      {hoveredCitation && (
        <CitationPopover
          onView={cit => {
            setActiveCitation(cit);
            publish(EventType.NAVIGATE_TO_DOCUMENT_REFERENCE, {
              documentReferenceId: cit.document_reference,
              expandDrawer: true,
              activeCitationData: cit,
            });
          }}
          open={Boolean(hoveredCitation)}
          anchorEl={anchorEl}
          onClose={scheduleClose}
          citation={hoveredCitation}
          columnarIdentifier={titleCell}
          onDelete={handleCitationDelete}
          slotProps={{
            paper: {
              onMouseEnter: cancelClose,
              onMouseLeave: scheduleClose,
            },
          }}
        />
      )}
    </StyledCitationValueViewerWrapper>
  );
};

export default CitationValueViewer;
